import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import eventsApi from 'api/requests/events';
import { useEvent } from 'context/events/Event';

/**
 * Simulação da taxa de serviço para o produtor precificar o ingresso.
 *
 * A conta é do backend (POST /events/fees/simulate), a mesma da cotação do
 * pedido: o front não tem fórmula. É uma estimativa para um pedido de UM
 * ingresso àquele preço — o mínimo da taxa é por pedido, então quem compra
 * vários paga menos por ingresso.
 *
 * Sem cache entre telas: as respostas ficam só na instância do hook, então
 * toda vez que a tela abre a taxa vem do backend e nunca é a de um valor
 * antigo. Quem usa deve chamar o hook UMA vez, na tela, com todos os preços,
 * para sair uma requisição só.
 *
 * `null` em `feeFor` significa "ainda sem resposta" (ou falhou): a tela deve
 * mostrar "—" em vez de um número. Uma taxa errada com cara de certa é pior
 * que taxa nenhuma, porque o produtor precifica em cima dela.
 */
const DEBOUNCE_MS = 350;
const MAX_ITEMS = 50;
const ZERO = { credit_card: { service_fee: 0, net: 0 }, pix: { service_fee: 0, net: 0 } };

const cents = (value) => Math.round((Number(value) || 0) * 100) / 100;
const keyOf = (slug, price) => `${slug || ''}|${cents(price).toFixed(2)}`;

function normalize(entry) {
    return {
        price: Number(entry.price),
        credit_card: {
            service_fee: Number(entry.credit_card?.service_fee) || 0,
            net: Number(entry.credit_card?.net) || 0,
        },
        pix: {
            service_fee: Number(entry.pix?.service_fee) || 0,
            net: Number(entry.pix?.net) || 0,
        },
    };
}

function simulate(slug, items) {
    return eventsApi.simulateFees({ ...(slug ? { event: slug } : {}), items })
        .then(({ data }) => data.data.map(normalize));
}

/**
 * O caminho inverso: o preço que entrega ao menos o líquido pedido (calculado
 * sobre o cartão, a taxa mais alta).
 */
export async function suggestPriceFromNet(slug, net) {
    const [result] = await simulate(slug, [{ net: cents(net) }]);

    return result;
}

/**
 * @param {number[]} prices preços a simular; os de R$ 0 não vão à API.
 * @returns {{ ready: boolean, feeFor: (price: number) => object | null }}
 */
export default function useFeeSimulation(prices) {
    const { event } = useEvent();
    const slug = event?.slug;
    const results = useRef(new Map());
    const mounted = useRef(true);
    const [, setVersion] = useState(0);

    useEffect(() => () => { mounted.current = false; }, []);

    // Preços positivos e únicos, em ordem estável: a chave evita requisição
    // nova quando só a identidade do array muda.
    const pricesKey = [...new Set(prices.map(cents).filter((price) => price > 0))].sort((a, b) => a - b).join(',');
    const wanted = useMemo(() => (pricesKey ? pricesKey.split(',').map(Number) : []), [pricesKey]);

    useEffect(() => {
        const missing = wanted.filter((price) => !results.current.has(keyOf(slug, price)));

        if (missing.length === 0) {
            return undefined;
        }

        // Um produtor digitando dispara um preço por tecla: espera parar. Se o
        // preço mudar antes, este timer cai e o próximo pede só o que faltar.
        const timer = setTimeout(async () => {
            for (let i = 0; i < missing.length; i += MAX_ITEMS) {
                const chunk = missing.slice(i, i + MAX_ITEMS);

                try {
                    const answers = await simulate(slug, chunk.map((price) => ({ price })));

                    // A resposta vale mesmo que o produtor já tenha mudado o
                    // preço: a chave é o preço, não a posição na tela.
                    chunk.forEach((price, index) => results.current.set(keyOf(slug, price), answers[index]));
                } catch (error) {
                    // Falha não é guardada: a próxima mudança de preço tenta de novo.
                    break;
                }
            }

            if (mounted.current) {
                setVersion((version) => version + 1);
            }
        }, DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [slug, wanted]);

    const feeFor = useCallback((price) => {
        if (cents(price) <= 0) {
            return ZERO;
        }

        return results.current.get(keyOf(slug, price)) ?? null;
    }, [slug]);

    return {
        ready: wanted.every((price) => results.current.has(keyOf(slug, price))),
        feeFor,
    };
}
